import { useSyncExternalStore } from 'react';
import { GAME_IDS, type GameId } from '@gmp/shared-types/browser';

export type Route =
  | { name: 'home' }
  | { name: 'create'; game?: GameId }
  | { name: 'join'; code?: string }
  | { name: 'how' }
  | { name: 'settings' }
  | { name: 'privacy' }
  | { name: 'display'; code: string }
  | { name: 'party' }
  | { name: 'parties' }
  | { name: 'device' }
  | { name: 'friends' }
  | { name: 'auth' }
  | { name: 'admin' };

export function parse(loc: Location = window.location): Route {
  const p = loc.pathname.replace(/\/+$/, '');
  // Codes are 4 digits (5 if the 4-digit space is full); parties from before keep 6 characters.
  let m = p.match(/^\/join\/([A-Za-z0-9]{4,6})$/);
  if (m) return { name: 'join', code: m[1]!.toUpperCase() };
  // Older audience links end in #k=…; the key is ignored (the code alone opens the audience screen).
  m = p.match(/^\/display\/([A-Za-z0-9]{4,6})$/);
  if (m) return { name: 'display', code: m[1]!.toUpperCase() };
  if (p === '/join') return { name: 'join' };
  if (p === '/create') {
    const g = new URLSearchParams(loc.search).get('game');
    return { name: 'create', game: (GAME_IDS as readonly string[]).includes(g ?? '') ? (g as GameId) : undefined };
  }
  if (p === '/how-to-play') return { name: 'how' };
  if (p === '/settings') return { name: 'settings' };
  if (p === '/privacy') return { name: 'privacy' };
  if (p === '/party') return { name: 'party' };
  if (p === '/parties') return { name: 'parties' };
  if (p === '/device') return { name: 'device' };
  if (p === '/friends' || p === '/social') return { name: 'friends' };
  if (p === '/auth/done') return { name: 'auth' };
  if (p === '/admin') return { name: 'admin' };
  return { name: 'home' };
}

const PATHS: Record<Route['name'], (r: Route) => string> = {
  home: () => '/',
  create: (r) => ((r as { game?: GameId }).game ? `/create?game=${(r as { game: GameId }).game}` : '/create'),
  join: (r) => ((r as { code?: string }).code ? `/join/${(r as { code: string }).code}` : '/join'),
  how: () => '/how-to-play',
  settings: () => '/settings',
  privacy: () => '/privacy',
  display: (r) => `/display/${(r as { code: string }).code}`,
  party: () => '/party',
  parties: () => '/parties',
  device: () => '/device',
  friends: () => '/social',
  auth: () => '/auth/done',
  admin: () => '/admin',
};

let current = parse();
const listeners = new Set<() => void>();
window.addEventListener('popstate', () => {
  current = parse();
  listeners.forEach((l) => l());
});

export function navigate(r: Route, replace = false) {
  const path = PATHS[r.name](r);
  if (replace) history.replaceState(null, '', path);
  else history.pushState(null, '', path);
  current = r;
  listeners.forEach((l) => l());
  window.scrollTo(0, 0);
}

export function useRoute(): Route {
  return useSyncExternalStore(
    (fn) => {
      listeners.add(fn);
      return () => listeners.delete(fn);
    },
    () => current,
  );
}
